import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ListingCard } from '@/components/ListingCard';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { LocationSelect } from '@/components/LocationSelect';
import { CANADIAN_LOCATIONS, normalizeLocationSearch } from '@/lib/canadianLocations';
import { GENDER_OPTIONS, HYPOALLERGENIC_OPTIONS, normalizeSelectValue } from '@/lib/listingAttributes';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { SEO } from '@/components/SEO';

const PRICE_MAX = 20000;
const PAGE_SIZE = 20;
const CLIENT_FETCH_LIMIT = 5000;

const PROVINCE_CODE_MAP = CANADIAN_LOCATIONS.reduce((acc, item) => {
  acc[normalizeLocationSearch(item.abbreviation)] = item.province;
  acc[normalizeLocationSearch(item.province)] = item.province;
  return acc;
}, {});

function getProvinceByName(provinceName) {
  return CANADIAN_LOCATIONS.find(item => item.province === provinceName);
}

function getLocationFilterValue(provinceName, cityName) {
  if (provinceName && cityName) return `${cityName}, ${provinceName}`;
  return provinceName || '';
}

function getListingLocationText(location) {
  if (location && typeof location === 'object') {
    return [location.text, location.city, location.province, location.state].filter(Boolean).join(' ');
  }
  return String(location || '');
}

function parseInitialLocation(value) {
  const normalized = normalizeLocationSearch(value);
  if (!normalized) return { province: '', city: '' };
  for (const provinceInfo of CANADIAN_LOCATIONS) {
    const province = normalizeLocationSearch(provinceInfo.province);
    const abbreviation = normalizeLocationSearch(provinceInfo.abbreviation);
    const matchedCity = provinceInfo.cities.find(city => normalized.includes(normalizeLocationSearch(city)));
    if (matchedCity && (normalized.includes(province) || normalized.includes(abbreviation))) {
      return { province: provinceInfo.province, city: matchedCity };
    }
    if (normalized === province || normalized === abbreviation || normalized.includes(province)) {
      return { province: provinceInfo.province, city: '' };
    }
  }
  return { province: '', city: '' };
}

function levenshteinDistance(a, b) {
  if (a === b) return 0;
  if (!a) return b.length;
  if (!b) return a.length;
  const row = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i += 1) {
    let previous = row[0];
    row[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const temp = row[j];
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, previous + (a[i - 1] === b[j - 1] ? 0 : 1));
      previous = temp;
    }
  }
  return row[b.length];
}

function listingMatchesSearch(listing, searchValue) {
  const query = normalizeLocationSearch(searchValue);
  if (!query) return true;
  const haystack = normalizeLocationSearch([
    listing.title, listing.breed,
    getListingLocationText(listing.location),
    listing.description, listing.gender, listing.hypoallergenic,
  ].filter(Boolean).join(' '));
  if (haystack.includes(query)) return true;
  const words = haystack.split(' ').filter(word => word.length >= 3);
  return query.split(' ').some(queryWord => {
    if (queryWord.length < 3) return false;
    const allowedDistance = queryWord.length <= 5 ? 1 : 2;
    return words.some(word => Math.abs(word.length - queryWord.length) <= allowedDistance && levenshteinDistance(queryWord, word) <= allowedDistance);
  });
}

function listingMatchesLocation(listing, provinceName, cityName) {
  if (!provinceName) return true;
  const provinceInfo = getProvinceByName(provinceName);
  const rawLocation = getListingLocationText(listing.location);
  const locationText = normalizeLocationSearch(rawLocation);
  const descriptionText = normalizeLocationSearch(listing.description || '');
  const listingProvince = typeof listing.location === 'object'
    ? PROVINCE_CODE_MAP[normalizeLocationSearch(listing.location.province || listing.location.state || '')]
    : '';
  if (cityName) {
    const city = normalizeLocationSearch(cityName);
    return locationText.includes(city) || descriptionText.includes(city);
  }
  const province = normalizeLocationSearch(provinceName);
  const abbreviation = provinceInfo?.abbreviation || '';
  if (listingProvince === provinceName) return true;
  if (locationText.includes(province)) return true;
  if (abbreviation && new RegExp(`\\b${abbreviation}\\b`, 'i').test(rawLocation)) return true;
  return Boolean(provinceInfo?.cities?.some(city => locationText.includes(normalizeLocationSearch(city))));
}

function sortListings(list, sortOrder) {
  const copy = [...list];
  if (sortOrder === 'price_low') {
    copy.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
  } else if (sortOrder === 'price_high') {
    copy.sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
  } else if (sortOrder === 'oldest') {
    copy.sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')));
  } else {
    // newest - preserve server order by default (server already returns newest-first)
    // only sort if needed
    copy.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
  }
  return copy;
}

function clientFilterListings(listings, filters) {
  const { search, breed, size, province, city, gender, hypoallergenic, priceRange } = filters;
  const normalizedBreed = normalizeLocationSearch(breed);
  return listings.filter((listing) => {
    if (!listingMatchesSearch(listing, search)) return false;
    if (normalizedBreed && !normalizeLocationSearch(listing.breed).includes(normalizedBreed)) return false;
    if (size && normalizeSelectValue(listing.size, '') !== size) return false;
    if (!listingMatchesLocation(listing, province, city)) return false;
    if (gender && normalizeSelectValue(listing.gender) !== gender) return false;
    if (hypoallergenic && normalizeSelectValue(listing.hypoallergenic) !== hypoallergenic) return false;
    const price = Number(listing.price || 0);
    if (priceRange?.[0] > 0 && price < priceRange[0]) return false;
    if (priceRange?.[1] < PRICE_MAX && price > priceRange[1]) return false;
    return true;
  });
}

export default function MarketplacePage() {
  const [searchParams] = useSearchParams();
  const initialLocation = parseInitialLocation(searchParams.get('location') || '');
  const { user } = useAuth();

  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [breedOptions, setBreedOptions] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [breed, setBreed] = useState(searchParams.get('breed') || '');
  const [size, setSize] = useState(searchParams.get('size') || '');
  const [gender, setGender] = useState(searchParams.get('gender') || '');
  const [hypoallergenic, setHypoallergenic] = useState(searchParams.get('hypoallergenic') || '');
  const [sort, setSort] = useState('newest');
  const [priceRange, setPriceRange] = useState([0, PRICE_MAX]);
  const [locationValue, setLocationValue] = useState({ province: initialLocation.province, city: initialLocation.city });
  const [page, setPage] = useState(1);

  // Master cache: fetch all listings once from server per sort-change.
  // Filters are applied purely client-side so listing order stays stable.
  const masterListingsRef = useRef([]);
  const cachedSortRef = useRef(null);
  const fetchInProgressRef = useRef(false);
  const latestFetchId = useRef(0);

  useEffect(() => {
    api.getBreedNames().then(r => setBreedOptions(r.data.names || [])).catch(() => {});
    if (user) {
      api.getFavoriteIds().then(r => setFavoriteIds(r.data.favorite_ids || [])).catch(() => {});
    }
  }, [user]);

  // Apply client-side filters on the cached master list and update display state.
  const applyFilters = useCallback((master, filters, effectivePage) => {
    const filtered = clientFilterListings(master, filters);
    const sorted = sortListings(filtered, filters.sort);
    const skip = (effectivePage - 1) * PAGE_SIZE;
    setListings(sorted.slice(skip, skip + PAGE_SIZE));
    setTotal(sorted.length);
    setPages(sorted.length > 0 ? Math.ceil(sorted.length / PAGE_SIZE) : 1);
    setLoading(false);
  }, []);

  // Fetch master listing list from server (only when sort order changes).
  const loadMasterListings = useCallback(async (sortOrder, filters, effectivePage) => {
    if (fetchInProgressRef.current && cachedSortRef.current === sortOrder) {
      // Same sort already loading - just wait
      return;
    }
    const fetchId = latestFetchId.current + 1;
    latestFetchId.current = fetchId;
    fetchInProgressRef.current = true;
    setLoading(true);
    try {
      const res = await api.getListings({ page: 1, limit: CLIENT_FETCH_LIMIT, sort: sortOrder });
      if (fetchId !== latestFetchId.current) return;
      const master = res.data.listings || [];
      masterListingsRef.current = master;
      cachedSortRef.current = sortOrder;
      applyFilters(master, filters, effectivePage);
    } catch {
      if (fetchId === latestFetchId.current) {
        setListings([]);
        setLoading(false);
      }
    } finally {
      if (fetchId === latestFetchId.current) {
        fetchInProgressRef.current = false;
      }
    }
  }, [applyFilters]);

  // Main entry point: use cached master if sort hasn't changed; otherwise re-fetch.
  const fetchListings = useCallback((overrides = {}) => {
    const effectiveSort = overrides.sort ?? sort;
    const effectivePage = overrides.page ?? page;
    const filters = {
      search:          overrides.search          ?? search,
      breed:           overrides.breed           ?? breed,
      size:            overrides.size            ?? size,
      province:        overrides.province        ?? locationValue.province,
      city:            overrides.city            ?? locationValue.city,
      gender:          overrides.gender          ?? gender,
      hypoallergenic:  overrides.hypoallergenic  ?? hypoallergenic,
      priceRange:      overrides.priceRange      ?? priceRange,
      sort:            effectiveSort,
    };

    if (cachedSortRef.current === effectiveSort && masterListingsRef.current.length > 0) {
      // Re-filter client-side from cache — no server round-trip, no listing shuffle
      applyFilters(masterListingsRef.current, filters, effectivePage);
    } else {
      // Sort changed or first load — must fetch fresh from server
      loadMasterListings(effectiveSort, filters, effectivePage);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, page, search, breed, size, locationValue, gender, hypoallergenic, priceRange, applyFilters, loadMasterListings]);

  // Re-apply filters whenever any filter or page changes (uses cached master data when available)
  useEffect(() => {
    fetchListings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sort, breed, size, locationValue.province, locationValue.city, gender, hypoallergenic]);

  const handleLocationChange = (province, city) => { setLocationValue({ province, city }); setPage(1); };
  const handleBreedChange = (value) => { setBreed(value === 'all' ? '' : value); setPage(1); };
  const handleSizeChange = (value) => { setSize(value === 'all' ? '' : value); setPage(1); };
  const handleGenderChange = (value) => { setGender(value === 'all' ? '' : value); setPage(1); };
  const handleHypoallergenicChange = (value) => { setHypoallergenic(value === 'all' ? '' : value); setPage(1); };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchListings({ page: 1 });
  };

  const clearFilters = () => {
    const cleared = { page: 1, search: '', breed: '', size: '', province: '', city: '', gender: '', hypoallergenic: '', priceRange: [0, PRICE_MAX], sort: 'newest' };
    setSearch(''); setBreed(''); setSize('');
    setLocationValue({ province: '', city: '' });
    setGender(''); setHypoallergenic('');
    setPriceRange([0, PRICE_MAX]);
    setSort('newest'); setPage(1);
    fetchListings(cleared);
  };

  const hasActiveFilters = breed || size || locationValue.province || locationValue.city || gender || hypoallergenic || priceRange[0] > 0 || priceRange[1] < PRICE_MAX;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in" data-testid="marketplace-page">
      <SEO
        title="Dogs for Sale"
        description="Browse puppies and dogs for sale from trusted breeders near you. Filter by breed, price, location, and size."
        keywords="dogs for sale near me, puppies for sale, dog breeders near me, buy a dog online, puppy marketplace"
      />
      <div className="mb-8">
        <h1 className="font-['Nunito'] font-extrabold text-3xl sm:text-4xl text-[#1A1D20] tracking-tight">Dogs for Sale</h1>
        <p className="text-sm text-[#5C636A] mt-1">Browse puppies and dogs from trusted breeders near you</p>
      </div>

      {/* Search & Filter Bar — relative+z-10 keeps the location dropdown above listing cards */}
      <div className="relative z-10 bg-white rounded-2xl p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] mb-6">
        <form onSubmit={handleSearch} className="flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C636A]" />
            <Input
              placeholder="Search by breed, name, location..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-11 rounded-xl bg-gray-50 border-gray-200"
              data-testid="marketplace-search-input"
            />
          </div>
          <Button type="submit" className="h-11 rounded-xl bg-[#4A90E2] hover:bg-[#3a7bc8] text-white font-semibold px-6" data-testid="marketplace-search-button">
            Search
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className={`h-11 rounded-xl px-4 ${showFilters ? 'bg-blue-50 border-[#4A90E2] text-[#4A90E2]' : ''}`}
            data-testid="toggle-filters-button"
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" /> Filters
            {hasActiveFilters && <span className="ml-1 w-2 h-2 rounded-full bg-[#F5A623]" />}
          </Button>
        </form>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 animate-fade-in" data-testid="filters-panel">
            <div>
              <label className="text-xs font-medium text-[#5C636A] mb-1.5 block">Breed</label>
              <Select value={breed || 'all'} onValueChange={handleBreedChange}>
                <SelectTrigger className="rounded-xl bg-gray-50" data-testid="filter-breed-select">
                  <SelectValue placeholder="All Breeds" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="all">All Breeds</SelectItem>
                  {breedOptions.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#5C636A] mb-1.5 block">Size</label>
              <Select value={size || 'all'} onValueChange={handleSizeChange}>
                <SelectTrigger className="rounded-xl bg-gray-50" data-testid="filter-size-select">
                  <SelectValue placeholder="Any Size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Size</SelectItem>
                  <SelectItem value="small">Small</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="large">Large</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#5C636A] mb-1.5 block">Location</label>
              <LocationSelect
                value={locationValue}
                onChange={handleLocationChange}
                placeholder="All Provinces"
                testId="filter-location-select"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5C636A] mb-1.5 block">Gender</label>
              <Select value={gender || 'all'} onValueChange={handleGenderChange}>
                <SelectTrigger className="rounded-xl bg-gray-50" data-testid="filter-gender-select">
                  <SelectValue placeholder="Any Gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Gender</SelectItem>
                  {GENDER_OPTIONS.map(option => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#5C636A] mb-1.5 block">Hypoallergenic</label>
              <Select value={hypoallergenic || 'all'} onValueChange={handleHypoallergenicChange}>
                <SelectTrigger className="rounded-xl bg-gray-50" data-testid="filter-hypoallergenic-select">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  {HYPOALLERGENIC_OPTIONS.map(option => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 lg:col-span-2">
              <label className="text-xs font-medium text-[#5C636A] mb-1.5 block">
                Price: ${priceRange[0].toLocaleString()} – ${priceRange[1].toLocaleString()}
              </label>
              {/* onValueChange updates the label live; onValueCommit fires filter on release only */}
              <Slider
                min={0}
                max={PRICE_MAX}
                step={100}
                value={priceRange}
                onValueChange={setPriceRange}
                onValueCommit={(value) => {
                  setPage(1);
                  fetchListings({ page: 1, priceRange: value });
                }}
                className="mt-3"
                data-testid="filter-price-slider"
              />
            </div>
            <div className="sm:col-span-2 lg:col-span-6 flex justify-end items-center">
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-sm text-[#5C636A]" data-testid="clear-filters-button">
                <X className="w-3.5 h-3.5 mr-1" /> Clear All Filters
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Sort & Results Count */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-[#5C636A]">{loading ? 'Loading...' : `${total} dogs found`}</p>
        <Select value={sort} onValueChange={(v) => { setSort(v); setPage(1); }}>
          <SelectTrigger className="w-[160px] rounded-xl bg-white" data-testid="sort-select">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="oldest">Oldest First</SelectItem>
            <SelectItem value="price_low">Price: Low to High</SelectItem>
            <SelectItem value="price_high">Price: High to Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Listings Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white rounded-3xl overflow-hidden animate-pulse">
              <div className="aspect-square bg-gray-200" />
              <div className="p-5 space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : listings.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="listings-grid">
          {listings.map((listing) => (
            <ListingCard key={listing.listing_id} listing={listing} isFavorited={favoriteIds.includes(listing.listing_id)} />
          ))}
        </div>
      ) : (
        <div className="text-center py-20" data-testid="no-listings-message">
          <p className="text-[#5C636A] text-lg mb-2">No dogs found</p>
          <p className="text-sm text-[#5C636A]">Try adjusting your filters or search terms</p>
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex justify-center gap-2 mt-8" data-testid="pagination">
          {[...Array(pages)].map((_, i) => (
            <Button
              key={i}
              variant={page === i + 1 ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPage(i + 1)}
              className={`rounded-lg ${page === i + 1 ? 'bg-[#4A90E2] text-white' : ''}`}
            >
              {i + 1}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
